import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { PlaceholderCopyList } from '@/components/patientReports/PlaceholderCopyList';
import { placeTextCodePanel } from '@/components/patientReports/textCodePanel';
import { catalogPlaceholders } from '@/lib/patientReports/placeholders';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const TextPlaceholderContext = createContext<{
  open: boolean;
  toggle: () => void;
  panelId: string;
} | null>(null);

export function TextPlaceholderProvider({
  anchorRef,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const titleId = `${panelId}-title`;

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const panel = panelRef.current;
      const column = anchorRef.current?.closest('[data-testid="report-sources-panel"]');
      if (!panel || !(column instanceof HTMLElement)) return;
      const rect = column.getBoundingClientRect();
      const box = placeTextCodePanel(
        { top: rect.top, right: rect.right },
        { width: window.innerWidth, height: window.innerHeight },
      );
      panel.style.top = `${box.top}px`;
      panel.style.left = `${box.left}px`;
      panel.style.width = `${box.width}px`;
      panel.style.maxHeight = `${box.maxHeight}px`;
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-placeholder-toggle]')) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <TextPlaceholderContext.Provider
      value={{ open, toggle: () => setOpen((current) => !current), panelId }}
    >
      {children}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              data-testid="report-placeholder-panel"
              role="region"
              aria-labelledby={titleId}
              className="pr-placeholder-panel"
            >
              <div>
                <h2 id={titleId} className={cn(TYPE.section, 'text-ds-text')}>
                  {t('patientReports.placeholders_panel_title')}
                </h2>
                <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                  {t('patientReports.placeholders_panel_hint')}
                </p>
              </div>
              <PlaceholderCopyList items={catalogPlaceholders()} layout="stack" />
            </div>,
            document.body,
          )
        : null}
    </TextPlaceholderContext.Provider>
  );
}

export function TextPlaceholderToggle() {
  const { t } = useTranslation();
  const ctx = useContext(TextPlaceholderContext);
  if (!ctx) return null;
  return (
    <div className="mt-s1 flex justify-end">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="pr-code-toggle"
        data-testid="report-placeholder-toggle"
        data-placeholder-toggle=""
        aria-expanded={ctx.open}
        aria-controls={ctx.panelId}
        aria-label={
          ctx.open ? t('patientReports.placeholders_close') : t('patientReports.placeholders_open')
        }
        onClick={ctx.toggle}
      >
        <ChevronRight
          aria-hidden
          strokeWidth={1.75}
          className={cn('transition-transform', ctx.open && 'rotate-180')}
        />
      </Button>
    </div>
  );
}
